import { Component, OnDestroy, OnInit } from '@angular/core';
import { AuthService } from '../../services/auth/auth-service';
import { MessageControllerService } from '../../services/message-controller.service';
import { Message } from '../../models/message';
import { filter, Subscription, take } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { WebsocketService } from '../../services/websocket.service';
import { WebSocketMessageType } from '../../enums';
import { GlobalStateProviderService } from '../../services/global-state-provider.service';
import { BpaSettingsService } from '../../services/bpa-settings.service';
import { BPASettings } from '@models/bpa-settings';
import { NavigationEnd, Router } from '@angular/router';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html'
})
export class DashboardComponent implements OnInit, OnDestroy {
  alertsHidden: boolean = false;
  moreThan3MessagesNotification: SafeHtml | null = null;
  hideListOfServices: boolean = false;
  hideMyApplications: boolean = false;
  showMyAccountPage: boolean = true;
  private subscription?: Subscription;
  private bpaSettingsSubscription?: Subscription;
  private routerSubscription?: Subscription;

  constructor(
    public authService: AuthService,
    private readonly router: Router,
    public messageController: MessageControllerService,
    private readonly webSocket: WebsocketService,
    private readonly sanitizer: DomSanitizer,
    private readonly bpaSettings: BpaSettingsService,
    public globalStateProviderService: GlobalStateProviderService
  ) {}

  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  ngOnInit(): void {
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.showMyAccountPage = this.shouldShowMyAccountPage(
          (event as NavigationEnd).url
        );
      });

    this.bpaSettingsSubscription = this.bpaSettings
      .getBpaSettings$()
      .subscribe((bpaSettings: BPASettings | null) => {
        if (bpaSettings) {
          this.hideListOfServices =
            !!bpaSettings.displaySystemProperties?.hide_list_of_services;
          this.hideMyApplications =
            !!bpaSettings.displaySystemProperties?.hide_applications_list;
          this.updateHtmlClasses();
        }
      });

    this.moreThan3MessagesNotification = this.sanitizeHtml(
      $localize`:@@You have notifications, check <strong>My messages</strong>:You have notifications, check <strong>My messages</strong>`
    );
    this.authService.isLoggedIn$.pipe(take(1)).subscribe((isLoggedIn) => {
      if (!isLoggedIn) return;
      this.getAlerts();
    });

    this.subscription = this.webSocket.messageReceived$.subscribe((message) => {
      if (message?.type !== WebSocketMessageType.NEW_ALERT_MESSAGE) return;
      this.getAlerts();
    });
  }

  private getAlerts() {
    this.messageController.getAlerts().subscribe((messages) => {
      this.globalStateProviderService.alerts = messages;
    });
  }

  hideAlert(alert: Message | null = null) {
    if (!alert) {
      this.alertsHidden = true;
      return;
    }
    this.messageController.deleteAlert(alert).subscribe(() => {
      this.getAlerts();
    });
  }

  private updateHtmlClasses() {
    const htmlTag = document.documentElement;
    if (this.hideMyApplications) {
      htmlTag.classList.add('applications-list-hidden');
    } else {
      htmlTag.classList.remove('applications-list-hidden');
    }
    if (this.hideListOfServices) {
      htmlTag.classList.add('services-list-hidden');
    } else {
      htmlTag.classList.remove('services-list-hidden');
    }
  }

  private shouldShowMyAccountPage(url: string): boolean {
    return (
      !url.includes('/services/') &&
      !url.includes('blob:') &&
      !url.includes('/part-b') &&
      !url.includes('download')
    );
  }

  public ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.bpaSettingsSubscription) {
      this.bpaSettingsSubscription.unsubscribe();
    }
    // Clean up HTML classes when component is destroyed
    document.documentElement.classList.remove(
      'applications-list-hidden',
      'services-list-hidden'
    );
  }
}
